import { defineAsyncComponent } from 'vue';
import { DEFAULT_LANDING_VERSION } from '@/core/landing/landingVersionContext';
import HeroFeatures from '@/components/landing/HeroFeatures.vue';
import HeroFeaturesB from '@/components/landing/HeroFeaturesB.vue';
import LandingAdvantages from '@/components/landing/LandingAdvantages.vue';
import LandingAdvantagesB from '@/components/landing/LandingAdvantagesB.vue';
import LandingSteps from '@/components/landing/LandingSteps.vue';
import LandingFooter from '@/components/landing/LandingFooter.vue';

// 统一的异步 Section 包装器：与原 LandingPageView 中的 createLandingAsyncSection 行为一致
// （suspensible: false），保持现有异步加载策略不变。
const createAsyncSection = (loader) =>
  defineAsyncComponent({
    loader,
    suspensible: false,
  });

// 保留原有 webpackChunkName，确保异步 chunk 名与基线完全一致：
// landing-use-cases / landing-pricing / landing-faq / landing-blog
const LandingUseCases = createAsyncSection(() =>
  import(
    /* webpackChunkName: "landing-use-cases" */ '@/components/landing/LandingUseCases.vue'
  )
);
const PricingSection = createAsyncSection(() =>
  import(
    /* webpackChunkName: "landing-pricing" */ '@/components/landing/PricingSection.vue'
  )
);
const FAQ = createAsyncSection(() =>
  import(/* webpackChunkName: "landing-faq" */ '@/components/landing/FAQ.vue')
);
const BlogSection = createAsyncSection(() =>
  import(
    /* webpackChunkName: "landing-blog" */ '@/components/landing/BlogSection.vue'
  )
);

// Section 组件目录：唯一编排事实源（方案 §4.1 / §4.2）。
// 每个条目的字段契约：
//   key            - Section 目录唯一 key（B 独有项使用业务名或 *B 后缀）
//   id             - 组件根 Section 的 DOM id（页面锚点 + Observer 目标）
//   component      - 同步组件或已包装的异步组件
//   landingSection - 埋点稳定 code
//   sourcePosition - 埋点稳定位置 code
//   headerLabelKey - 进入 Header 导航时的 i18n key；不进入时明确为 null
const SECTION_CATALOG = Object.freeze({
  heroA: Object.freeze({
    key: 'heroA',
    id: 'features',
    component: HeroFeatures,
    landingSection: 'features',
    sourcePosition: 'landing_hero',
    headerLabelKey: 'header.landing.features',
  }),
  heroB: Object.freeze({
    key: 'heroB',
    id: 'features',
    component: HeroFeaturesB,
    landingSection: 'features',
    sourcePosition: 'landing_hero',
    headerLabelKey: 'header.landing.features',
  }),
  advantagesA: Object.freeze({
    key: 'advantagesA',
    id: 'advantages',
    component: LandingAdvantages,
    landingSection: 'advantages',
    sourcePosition: 'landing_advantages',
    headerLabelKey: null,
  }),
  advantagesB: Object.freeze({
    key: 'advantagesB',
    id: 'advantages',
    component: LandingAdvantagesB,
    landingSection: 'advantages',
    sourcePosition: 'landing_advantages',
    headerLabelKey: null,
  }),
  stepsA: Object.freeze({
    key: 'stepsA',
    id: 'steps',
    component: LandingSteps,
    landingSection: 'steps',
    sourcePosition: 'landing_steps',
    headerLabelKey: null,
  }),
  useCases: Object.freeze({
    key: 'useCases',
    id: 'use-cases',
    component: LandingUseCases,
    landingSection: 'use_cases',
    sourcePosition: 'landing_use_cases',
    headerLabelKey: 'header.landing.solutions',
  }),
  pricing: Object.freeze({
    key: 'pricing',
    id: 'pricing',
    component: PricingSection,
    landingSection: 'pricing',
    sourcePosition: 'landing_pricing',
    headerLabelKey: 'header.landing.pricing',
  }),
  faq: Object.freeze({
    key: 'faq',
    id: 'faq',
    component: FAQ,
    landingSection: 'faq',
    sourcePosition: 'landing_faq',
    headerLabelKey: 'header.landing.faq',
  }),
  blog: Object.freeze({
    key: 'blog',
    id: 'blog',
    component: BlogSection,
    landingSection: 'blog',
    sourcePosition: 'landing_blog',
    headerLabelKey: null,
  }),
  footer: Object.freeze({
    key: 'footer',
    id: 'contact',
    component: LandingFooter,
    landingSection: 'footer',
    sourcePosition: 'landing_footer',
    headerLabelKey: null,
  }),
});

// ─────────────────────────────────────────────────────────────────────────────
// 注释保留：原 LandingPageView 中曾以注释隐藏（未启用）的两个 Section。
// 迁移到 SECTION_CATALOG 时未纳入激活清单，但保留其原始元数据与组件线索，
// 供后续 B 设计（或重新启用）参考，避免信息丢失。
//
// 1) WaitList（waitlist）
//    - 组件：@/components/landing/WaitList.vue（原文件中 import 与模板 <WaitList /> 均被注释）
//    - 原 landingSections 注释条目：
//        { id: 'waitlist', landingSection: 'waitlist', sourcePosition: 'landing_waitlist' }
//    - 是否进入 Header 导航：未定义（原值为注释，保持 null 语义）
//    - headerLabelKey 建议：null
//
// 2) LandingTestimonials（testimonials）
//    - 组件：@/components/landing/LandingTestimonials.vue（原文件以 createLandingAsyncSection 包装，
//            且曾指定 webpackChunkName: "landing-testimonials"）
//    - 原 landingSections 注释条目：
//        { id: 'testimonials', landingSection: 'testimonials', sourcePosition: 'landing_testimonials' }
//    - 是否进入 Header 导航：未定义（原值为注释，保持 null 语义）
//    - headerLabelKey 建议：null
// ─────────────────────────────────────────────────────────────────────────────

// A/B 编排清单：仅声明各版本使用哪些 Section key 及排列顺序。
// B 基础设施首次合入时先显式引用与 A 相同的 key，
// 后续按 B 设计逐项替换、增加、删除和重排。
// 首个被替换的 B Section 为 heroB（HeroFeaturesB，替换 heroA）。
const VARIANT_SECTION_KEYS = Object.freeze({
  A: Object.freeze([
    'heroA',
    'advantagesA',
    'stepsA',
    'useCases',
    'pricing',
    'faq',
    'blog',
    'footer',
  ]),
  B: Object.freeze([
    'heroB',
    'advantagesB',
    'stepsA',
    'useCases',
    'pricing',
    'faq',
    'blog',
    'footer',
  ]),
});

export const LANDING_VERSIONS = Object.freeze(['A', 'B']);

// 根据版本取得完整 Section 配置数组；非法版本回退默认版本（当前为 B）。
export function getLandingSections(version) {
  const safeVersion = LANDING_VERSIONS.includes(version) ? version : DEFAULT_LANDING_VERSION;
  return VARIANT_SECTION_KEYS[safeVersion].map((key) => SECTION_CATALOG[key]);
}
